import { Suspense, useMemo } from 'react';
import Container from '@/components/Container';
import { BackButton } from '@/components/ui/back-button';
import DefaultContentTemplateMedia from '@/components/DefaultContentTemplate/Media';
import DefaultContentTemplate from '@/components/DefaultContentTemplate';
import DefaultContentTemplateAudio from '@/components/DefaultContentTemplate/Audio';
import TitleWithActions from '@/components/TitleWithActions';
import { formatPublicationDate, formatDurationFromSeconds } from '@/utils/date';
import type {
  ComponentCommonAudio,
  ComponentCommonMedia,
  Maybe,
  Podcast,
  PodcastDefaultContentTemplateDynamicZone,
  UploadFile,
} from '@edwin-group/people-graphql';
import { GENERATED, getRelatedPodcastsFromContent, getContentWithoutRelatedBlock } from '@edwin-group/people-graphql';
import PodcastCard from '@/pages/podcast/shared/components/podcast-card';
import { PodcastListenAt } from '@/pages/podcast/list/components/listen-at';
import Divider from '@/components/Divider';
import MediaDuration from '@/components/MediaDuration';
import { usePodcastDetail } from './hooks/usePodcastDetail';
import { usePodcastDetailStore } from './store/usePodcastDetailStore';
import { PodcastDetailLoading } from './components/loading';

const isAudioBlock = (
  c: PodcastDefaultContentTemplateDynamicZone | null,
): c is Extract<PodcastDefaultContentTemplateDynamicZone, { __typename: 'ComponentCommonAudio' }> =>
  c?.__typename === 'ComponentCommonAudio';

const PodcastDetailPageContent = () => {
  const { handleTimeUpdate, handlePlayAudio, listeningTime } = usePodcastDetail();
  const page = usePodcastDetailStore((state) => state.page);
  const current = usePodcastDetailStore((state) => state.current);
  const podcastListPage = usePodcastDetailStore((state) => state.podcastListPage);

  const backLabel = useMemo(() => page?.backLinkLabel || 'Back to Podcasts', [page?.backLinkLabel]);

  const mediaComponent = useMemo<ComponentCommonMedia | null>(() => {
    if (!current?.cover) return null;
    return {
      id: '1',
      media: {
        data: {
          attributes: {
            url: current.cover.url || '',
            alternativeText: current.cover.alternativeText || '',
          } as UploadFile,
        },
      },
    } as ComponentCommonMedia;
  }, [current?.cover]);

  const { firstAudioBlock, contentWithoutFirstAudio, relatedPodcasts } = useMemo(() => {
    const content = current?.DefaultContentTemplate ?? [];
    const contentWithoutRelated = getContentWithoutRelatedBlock(content);
    const audioIndex = contentWithoutRelated.findIndex(isAudioBlock);
    const firstAudio =
      audioIndex >= 0 && isAudioBlock(contentWithoutRelated[audioIndex]) ? contentWithoutRelated[audioIndex] : null;
    const contentWithoutFirstAudio =
      audioIndex >= 0
        ? contentWithoutRelated.filter(
            (_: Maybe<PodcastDefaultContentTemplateDynamicZone>, i: number) => i !== audioIndex,
          )
        : contentWithoutRelated;
    return {
      firstAudioBlock: firstAudio,
      contentWithoutFirstAudio,
      relatedPodcasts: getRelatedPodcastsFromContent(content),
    };
  }, [current?.DefaultContentTemplate]);

  const publicationDateFormatted = useMemo(
    () => formatPublicationDate(current?.publicationDate as string | null | undefined),
    [current?.publicationDate],
  );
  const duration = useMemo(() => {
    const listening = current?.listenings?.[0];
    return listening?.duration != null ? formatDurationFromSeconds(Number(listening.duration)) : null;
  }, [current?.listenings]);
  const description = (current?.fullDescription ?? current?.briefDescription) || '';

  if (!current) return null;

  return (
    <>
      <Container>
        <div className="col-span-12 lg:col-start-3 lg:col-end-11">
          <div className="mt-[64px]">
            <BackButton label={backLabel} to="/podcasts" />
          </div>
        </div>
      </Container>
      <Container>
        <div className="col-span-12 lg:col-start-3 lg:col-end-11">
          {mediaComponent && (
            <DefaultContentTemplateMedia
              className="highlight-image-border !mb-sh-15 mt-sh-30"
              component={mediaComponent}
            />
          )}
          <div className="mb-[8px] mt-[45px]">
            <TitleWithActions
              title={current.title || ''}
              entity={current as never}
              entityId={current.documentId || ''}
              like={false}
              favorite
              category={GENERATED.Enum_Bookmark_Category.Podcast}
              textSize="text-sh-30-38"
            />
          </div>
          {(publicationDateFormatted || duration) && (
            <div className="mb-[30px] flex flex-col gap-2 font-haffer text-sh-12-18 text-people-gray-400">
              {publicationDateFormatted && <span>{publicationDateFormatted}</span>}
              {duration && <MediaDuration duration={duration} />}
            </div>
          )}
          {description && <p className="mb-sh-30 font-haffer text-sh-16-24 text-dark-grey">{description}</p>}
          {firstAudioBlock && (
            <div className="mb-sh-30" key={current.documentId ?? undefined}>
              <DefaultContentTemplateAudio
                audio={firstAudioBlock as ComponentCommonAudio}
                handleTimeUpdate={handleTimeUpdate}
                listeningTime={listeningTime}
                onPlayAudio={handlePlayAudio}
                isDefaultContentTemplate
              />
            </div>
          )}
          <DefaultContentTemplate
            content={contentWithoutFirstAudio}
            handleTimeUpdate={handleTimeUpdate}
            listeningTime={listeningTime}
            onPlayAudio={handlePlayAudio}
            hideEmptyState={!!(firstAudioBlock || relatedPodcasts.length > 0)}
          />
          <div className="mt-sh-30">
            <PodcastListenAt label={podcastListPage?.listenAtLabel ?? null} platforms={current.platforms ?? null} />
          </div>
        </div>
      </Container>
      {relatedPodcasts.length > 0 && (
        <Container>
          <div className="col-span-12">
            <Divider />
            <h2 className="mb-sh-30 text-sh-24 font-bold">Related podcasts</h2>
            <div className="grid w-full grid-cols-1 gap-sh-30">
              {relatedPodcasts.map((podcast: Podcast) => (
                <PodcastCard
                  key={podcast.documentId}
                  podcast={podcast}
                  watchButtonLabel={podcastListPage?.card?.watchButtonLabel ?? null}
                  watchAgainButtonLabel={podcastListPage?.card?.watchAgainButtonLabel ?? null}
                />
              ))}
            </div>
          </div>
        </Container>
      )}
    </>
  );
};

export const PodcastDetailPage = () => {
  return (
    <Suspense fallback={<PodcastDetailLoading />}>
      <PodcastDetailPageContent />
    </Suspense>
  );
};
